import React, { useContext, useEffect, useState, useCallback } from "react";
import styled from "styled-components";
import WBCCollapse from "../elements/WBCCollapse";
import { AlertConstants } from "best-common-react";
import InterestListTable from "./InterestListTable";
import InterestListContext from "../../contexts/InterestListContext";
import RosterContext from "../../contexts/RosterContext";
import WBCYearsContext from "../../contexts/WBCYearsContext";
import SelectedTeamContext from "../../contexts/SelectedTeamContext";
import TournamentFormContext from "../../contexts/TournamentFormContext";
import PositionsContext from "../../contexts/PositionsContext";
import InterestListApi from "../../httpClients/InterestListApi";
import { useAlert } from "../hooks/useAlert";
import { useError } from "../hooks/useError";
import { sortPlayers } from "../roster/RosterSortHelper";
import TableSortFilterContext from "../../contexts/TableSortFilterContext";
import { WBCPrimaryButton } from "../elements/WBCPrimaryButton";
import GlobalModalContext from "../../contexts/GlobalModalContext";
import AuthContext from "../../contexts/AuthContext";
import { isStatusRequestRequired } from "../../utils/MLBProfileRequestUtil";
import { INTEREST_LIST } from "../../constants/RosterListConstants";
import { formatRosterDate } from "../../utils/DateUtils";
import { loadRoster as loadRosterUtil, updateTournamentRoster } from "../../utils/RosterUtils";
import useTournamentType from "../hooks/useTournamentType";
import CustomDownloadModal from "../shared/CustomDownloadModal";
import { INTEREST_LIST_ID } from "../../constants/TournamentStatusConstants";
import { MAIN_ID, QUALIFYING_ID } from "../../constants/TournamentTypeConstants";
import TabFilter from "../elements/TabFilter";
import { SubtitleText } from "../elements/CommonStyledComponents";

const Content = styled.div.attrs(() => ({
  className: "p-4"
}))`
  text-align: left;
  height: 74vh;
  overflow: scroll;
`;
const ButtonHolder = styled.div.attrs(() => ({
  className: "p-4"
}))`
  display: flex;
  flex-direction: row-reverse;
  margin-top: 1rem;
`;
const TextHolder = styled.div`
  display: flex;
  flex-direction: row-reverse;
`;

const InterestText = styled.span`
  font-family: Helvetica;
  font-size: 16px;
  color: ${props => props.theme.wbcBlue};
`;

const MaxText = styled.span`
  font-family: Helvetica;
  font-size: 12px;
  color: ${props => props.theme["dark-grey"]};
`;

const InterestListForm = () => {
  // context(s) and state
  const interestListContext = useContext(InterestListContext);
  const rosterContext = useContext(RosterContext);
  const globalModalContext = useContext(GlobalModalContext);
  const { selectedYear, selectedWbcYear } = useContext(WBCYearsContext);
  const selectedTeamContext = useContext(SelectedTeamContext);
  const tournamentFormContext = useContext(TournamentFormContext);
  const positionsContext = useContext(PositionsContext);
  const tableSortFilterContext = useContext(TableSortFilterContext);
  const { tournamentType, isMainTournament: isMainTournamentEnabled } = useTournamentType();
  const [interestList, setInterestList] = useState([]);
  const authContext = useContext(AuthContext);
  const [openDownloadModal, setOpenDownloadModal] = useState(false);
  const [filterTab, setFilterTab] = useState("all"); // "all" or "available"

  // hooks and variables
  const showAlert = useAlert();
  const showErrors = useError();
  const { posAbbrevToId } = positionsContext.state;
  const { onFormDirty, cleanForm } = tournamentFormContext.state;
  const { team } = selectedTeamContext.state;
  const { teamId: fedTeamId } = selectedTeamContext.state.team;
  const generalRosterSettings = selectedWbcYear && selectedWbcYear.generalRosterSettings;
  const [hasPlayersWithoutRequests, setHasPlayersWithoutRequests] = useState(false);
  const {
    positionUpdates,
    removedPlayers,
    displayedPlayers,
    pastWbcFlagUpdates,
    sortFilters,
    rosterSaved
  } = interestListContext.state;
  const { isWBCIadmin, isFedAdmin, isTeamCoordinator } = authContext.state;
  const canFinalize = isWBCIadmin || isTeamCoordinator || (isFedAdmin && !team.finalizedStatus?.finalizedInterestDate);
  const finalizedDate = team.finalizedStatus?.finalizedInterestDate;
  const rosterType = INTEREST_LIST;

  useEffect(() => {
    interestList.find(player => player.requestStatus !== null && isStatusRequestRequired(player.requestStatus))
      ? setHasPlayersWithoutRequests(true)
      : setHasPlayersWithoutRequests(false);
  }, [interestList]);

  // dispatches
  const { dispatch: interestDispatch } = interestListContext;
  const { dispatch: tournamentDispatch } = tournamentFormContext;

  // function(s)
  const loadRoster = useCallback(() => {
    if (fedTeamId && tournamentType) {
      return loadRosterUtil(tournamentType, fedTeamId, rosterContext.dispatch).then(() => {
        cleanForm();
      });
    }
    return Promise.resolve();
  }, [fedTeamId, tournamentType, rosterContext.dispatch, cleanForm]);

  const loadInterestList = useCallback(() => {
    if (fedTeamId && selectedYear && !rosterSaved) {
      InterestListApi.getInterestListPlayers(selectedYear, fedTeamId).then(response => {
        interestDispatch({
          type: "setPlayers",
          players: sortPlayers(sortFilters.col, sortFilters.dir, response)
        });
        cleanForm();
      });
    } else if (rosterSaved) {
      interestDispatch({
        type: "setRosterSaved",
        rosterSaved: false
      });
    }
  }, [selectedYear, fedTeamId, sortFilters, interestDispatch, rosterSaved, cleanForm]);

  const finalizeRoster = () => {
    if (hasPlayersWithoutRequests && !isMainTournamentEnabled) {
      showAlert(`MLB Requests still Required.`, "warning");
    }
    globalModalContext.dispatch({
      type: "openFinalizeRosterModal",
      headerText: `Finalize ${rosterType}`,
      content: { team, rosterType }
    });
  };

  const onSave = useCallback(
    (override = false) => {
      const posUpdates = Object.entries(positionUpdates).map(pu => {
        const [profileId, position] = pu;
        const positionId = posAbbrevToId[position];
        return {
          profileId,
          positionId
        };
      });
      const remPlayers = removedPlayers.map(rp => ({ profileId: rp }));
      const flagUpdate = Object.entries(pastWbcFlagUpdates).map(flg => {
        const [profileId, pastWbcFlag] = flg;
        return {
          profileId,
          pastWbcFlag
        };
      });

      // Get provisional roster data from RosterContext
      const { provisional } = rosterContext.state;

      // Save both interest list and roster updates
      Promise.all([
        InterestListApi.updateInterestListPlayers(
          fedTeamId,
          {
            positionUpdates: posUpdates,
            removedPlayers: remPlayers,
            pastWbcFlagUpdates: flagUpdate
          },
          override
        ),
        updateTournamentRoster(tournamentType, fedTeamId, provisional, null, true)
      ])
        .then(() => {
          loadInterestList();
          showAlert("Saved");
        })
        .catch(({ response }) => {
          interestDispatch({ type: "resetAfterError" });
          if (response.data.message === "Invalid update: Can't update Position of player on Final Roster") {
            showErrors(response.data.message, () => {
              onSave(true);
            });
          } else {
            showAlert(response.data.message, AlertConstants.TYPES.DANGER);
          }
        });
    },
    [
      showAlert,
      showErrors,
      loadInterestList,
      positionUpdates,
      posAbbrevToId,
      removedPlayers,
      pastWbcFlagUpdates,
      fedTeamId,
      interestDispatch,
      rosterContext.state.provisional,
      tournamentType
    ]
  );

  const handleCancel = useCallback(() => {
    // Reload both interest list and roster to reset all changes
    loadInterestList();
    loadRoster();
  }, [loadInterestList, loadRoster]);

  const exportFn = useCallback(() => {
    setOpenDownloadModal(true);
  }, []);

  // effect(s)
  // load roster on mount
  useEffect(() => {
    loadRoster();
  }, [loadRoster]);

  useEffect(() => {
    // Only check for Interest List specific changes (position updates, removed players, past WBC flags)
    // Provisional roster changes are handled by AddProvisionalFormatter calling onFormDirty() directly
    if (
      Object.keys(positionUpdates).length > 0 ||
      removedPlayers.length > 0 ||
      Object.keys(pastWbcFlagUpdates).length > 0
    ) {
      onFormDirty();
    }
    tournamentDispatch({ type: "setOnSave", onSave });
    tournamentDispatch({ type: "setOnCancel", onCancel: handleCancel });
  }, [positionUpdates, removedPlayers, pastWbcFlagUpdates, tournamentDispatch, onFormDirty, onSave, handleCancel]);

  useEffect(loadInterestList, [loadInterestList]);
  useEffect(() => {
    // Apply filter based on selected tab
    if (filterTab === "available") {
      // Filter to only show players with approved eligibility (eligible: "Yes")
      setInterestList(displayedPlayers.filter(player => player.eligible === "Yes"));
    } else {
      setInterestList(displayedPlayers);
    }
  }, [displayedPlayers, filterTab]);

  // components
  const collapseTitle = (
    <div className="d-flex">
      <div>
        <InterestText>Interest ({interestList.length})</InterestText>{" "}
        {generalRosterSettings && <MaxText>{generalRosterSettings.interestListMax} player max</MaxText>}
      </div>
      <div className="ml-auto">
        {/* <DownloadIcon
          onClick={event => {
            event.preventDefault();
            event.stopPropagation();
          }}
          /> */}
      </div>
    </div>
  );

  return (
    <Content>
      <WBCCollapse title={collapseTitle} exportable exportFn={exportFn}>
        <SubtitleText>Upload documentation and submit players for eligibility</SubtitleText>
        <TabFilter
          tabs={[
            { value: "all", label: "All Players" },
            { value: "available", label: "Available for Final Roster" }
          ]}
          activeTab={filterTab}
          onTabChange={setFilterTab}
        />
        <InterestListTable data={interestList} />
      </WBCCollapse>
      {canFinalize && (
        <ButtonHolder>
          <WBCPrimaryButton
            width={160}
            onClick={event => {
              event.stopPropagation();
              finalizeRoster();
            }}
          >
            Finalize {rosterType}
          </WBCPrimaryButton>
        </ButtonHolder>
      )}
      {!!finalizedDate && <TextHolder> Finalized on {formatRosterDate(finalizedDate)} </TextHolder>}
      <CustomDownloadModal
        tournamentType={isMainTournamentEnabled ? MAIN_ID : QUALIFYING_ID}
        fedTeam={fedTeamId}
        federation={team?.label}
        rosterType={INTEREST_LIST_ID}
        rosterName={INTEREST_LIST}
        isOpen={openDownloadModal}
        profileIds={interestList.map(player => player?.profileId)}
        onClose={() => setOpenDownloadModal(false)}
      />
    </Content>
  );
};

export default InterestListForm;
